import { cn } from '@/lib/utils.js'
import { Button } from '../ui/button.js'
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import {
  isSameDay,
  format,
  isToday,
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfWeek,
  isThisWeek,
  max,
  min,
  startOfDay,
  startOfToday,
  startOfWeek,
} from 'date-fns'
import { Variants, motion } from 'framer-motion'
import { AnimatedCheckbox } from './AnimatedCheckbox.jsx'
import { Record } from '@habitus/verdant'
import { hooks } from '@/store.js'

export const TimelineHeader = ({
  days,
  selectedDate,
  nextButtonAbsolute = true,
  onDayClick,
  onPrevClick,
  onNextClick,
}: {
  days: Date[]
  selectedDate: Date
  nextButtonAbsolute?: boolean
  onDayClick: (date: Date) => void
  onPrevClick: () => void
  onNextClick: () => void
}) => {
  const isNextButtonDisabled = isToday(days[days.length - 1])

  return (
    <div className="relative flex">
      <Button className="shrink-0" variant="ghost" size="icon" onClick={onPrevClick}>
        <ChevronLeftIcon />
      </Button>
      {days.map((day) => {
        const isActiveDay = isSameDay(selectedDate, day)

        return (
          <button
            key={String(day)}
            className={cn(
              'relative ml-2 flex w-8 shrink-0 cursor-pointer select-none flex-col items-center justify-center',
              !isActiveDay && isToday(day) && 'font-medium text-orange-500',
            )}
            onClick={() => {
              onDayClick(day)
            }}
          >
            {isActiveDay && (
              <motion.div
                layoutId="outline"
                className="absolute bottom-0 left-0 right-0 top-0 rounded bg-orange-300"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            )}
            <div className="z-10 text-center">
              <div className="text-sm">{format(day, 'EEE')}</div>
              <div className="text-xs">{format(day, 'd')}</div>
            </div>
          </button>
        )
      })}

      {(!nextButtonAbsolute || !isNextButtonDisabled) && (
        <Button
          disabled={isNextButtonDisabled}
          variant="ghost"
          size="icon"
          className={cn(
            'shrink-0',
            nextButtonAbsolute
              ? 'absolute bottom-0 right-0 top-0 -mr-2 translate-x-full transform'
              : 'ml-2',
          )}
          onClick={onNextClick}
        >
          <ChevronRightIcon />
        </Button>
      )}
    </div>
  )
}

const opacityVariants: Variants = {
  exit: {
    opacity: 0,
  },
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
  },
}

export const TimelineContent = ({
  days,
  habitId,
  records,
  className,
}: {
  days: Date[]
  habitId: string
  records: Record[]
  className?: string
}) => {
  const client = hooks.useClient()

  return (
    <motion.div
      className={cn('flex items-center justify-center', className)}
      exit="exit"
      initial="initial"
      animate="animate"
      variants={opacityVariants}
    >
      {days.map((day, index) => {
        const dateFormatted = format(day, 'yyyy.MM.dd')
        const activeRecord = records.find(
          (record) => record.get('parentId') === habitId && record.get('date') === dateFormatted,
        )

        return (
          <div key={index} className={cn('relative ml-2 flex h-8 w-8 items-center justify-center')}>
            <AnimatedCheckbox
              state={activeRecord?.get('status') || 'empty'}
              onClick={() => {
                if (activeRecord?.get('status') === 'done') {
                  client.records.delete(activeRecord.get('id'))
                } else {
                  client.records.put({
                    id: `${habitId}-${dateFormatted}`,
                    date: dateFormatted,
                    parentId: habitId,
                    status: 'done',
                    description: '',
                  })
                }
              }}
            />
          </div>
        )
      })}
    </motion.div>
  )
}

export const getSevenDays = (selectedDay: Date) => {
  if (isThisWeek(selectedDay, { weekStartsOn: 1 })) {
    return eachDayOfInterval({
      start: addDays(Date.now(), -6),
      end: Date.now(),
    })
  } else {
    return eachDayOfInterval({
      start: startOfWeek(selectedDay, { weekStartsOn: 1 }),
      end: endOfWeek(selectedDay, { weekStartsOn: 1 }),
    })
  }
}

export const getTimelineDays = (selectedDay: Date, number: number) => {
  selectedDay = startOfDay(selectedDay)
  const today = startOfToday()
  const daysDiff = differenceInCalendarDays(selectedDay, today)
  const periodIndex = Math.trunc(daysDiff / number)
  const daysToAdd = (Math.abs(periodIndex) + 1) * number - 1
  const leftSideDay = addDays(today, Math.sign(periodIndex) * daysToAdd)
  const rightSideDay = addDays(
    leftSideDay,
    Math.sign(periodIndex) === -1 ? number - 1 : -(number - 1),
  )

  return eachDayOfInterval({
    start: min([leftSideDay, rightSideDay]),
    end: max([leftSideDay, rightSideDay]),
  })
}
